import { EMPTY, from } from 'rxjs';
import assert from '@banking/ui/utils/assert';
import { BankAccountInfo } from '@square/dashboard-protos/models/squareup/deposits/api';
import { getAccountType, getBankAccountType, getLinkedBankAccountLabel } from '@banking/ui/utils/checking/utils';
import { ResendVerificationMessageRequest } from '@square/dashboard-protos/models/squareup/deposits/bank_account_service';
import { toggleChangeBankAccountModalAction } from '@banking/ui/workflows/bank-accounts/change-bank-account';
import { initCancelVerificationModalAction } from '@banking/ui/workflows/bank-accounts/cancel-verification';
import { openCheckingUpsellModalAction } from '@banking/ui/workflows/bank-accounts/checking-upsell';
import { getInitialPaginatedState, handleUserClickNextPageAction, handleUserClickPrevPageAction } from '@banking/ui/workflows/bank-accounts/pagination';
import { Action as EventAction, Applet, Feature, Page, Target, getEvent } from '@banking/ui/utils/checking/event-catalog';
import { AccountType } from '@square/dashboard-protos/models/squareup/banking/common';
import { Country } from '@square/dashboard-protos/models/squareup/common/countries';

// State
// =============================================================================

export const INITIAL_STATE = {
  currentPage: 1,
  displayedRows: [],
  lastPage: 1,
  merchantToken: '',
  requiresRefresh: false,
  allRows: [],
  showPagination: false,
  allLocationDescription: '',
  isAccountOwner: true
};
export const createState = (overrides = {}) => ({
  ...INITIAL_STATE,
  ...overrides
});

// Action
// =============================================================================

export let ActionType = /*#__PURE__*/function (ActionType) {
  ActionType["AddExternalBankAccountReceived"] = "ADD_EXTERNAL_BANK_ACCOUNT_RECEIVED";
  ActionType["InitAllLocations"] = "WORKFLOW_INIT_ALL_LOCATIONS";
  ActionType["OpenLinkBankAccountForm"] = "OPEN_LINK_BANK_ACCOUNT_FORM";
  ActionType["ResendEmailReceived"] = "RESEND_EMAIL_RECEIVED";
  ActionType["ResendEmailRejected"] = "RESEND_EMAIL_REJECTED";
  ActionType["UserClickAddExternalBankAccount"] = "USER_CLICK_ADD_EXTERNAL_BANK_ACCOUNT";
  ActionType["UserClickCancelVerification"] = "USER_CLICK_CANCEL_VERIFICATION";
  ActionType["UserClickChangeBankAccount"] = "USER_CLICK_CHANGE_BANK_ACCOUNT";
  ActionType["UserClickNextPage"] = "USER_CLICK_NEXT_PAGE";
  ActionType["UserClickPrevPage"] = "USER_CLICK_PREV_PAGE";
  ActionType["UserClickResendEmail"] = "USER_CLICK_RESEND_EMAIL";
  ActionType["UserClickUseChecking"] = "USER_CLICK_USE_CHECKING";
  return ActionType;
}({});
// Action Creators
// =============================================================================
export const addExternalBankAccountReceivedAction = () => ({
  type: ActionType.AddExternalBankAccountReceived
});
export const initAllLocationsAction = (merchantToken, unitBankAccountGroups, unitAccounts, isAccountOwner) => ({
  type: ActionType.InitAllLocations,
  merchantToken,
  unitBankAccountGroups,
  unitAccounts,
  isAccountOwner
});
export const openLinkBankAccountFormAction = (unitToken, itFee, merchantToken) => ({
  type: ActionType.OpenLinkBankAccountForm,
  unitToken,
  itFee,
  merchantToken
});
export const resendEmailReceivedAction = () => ({
  type: ActionType.ResendEmailReceived
});
export const resendEmailRejectedAction = error => ({
  type: ActionType.ResendEmailRejected,
  error
});
export const userClickAddExternalBankAccountAction = unitToken => ({
  type: ActionType.UserClickAddExternalBankAccount,
  unitToken
});
export const userClickCancelVerificationAction = (unitToken, pendingBankAccount) => ({
  type: ActionType.UserClickCancelVerification,
  unitToken,
  pendingBankAccount
});
export const userClickChangeBankAccountAction = (unitToken, locationName, merchantBankAccount) => ({
  type: ActionType.UserClickChangeBankAccount,
  unitToken,
  locationName,
  merchantBankAccount
});
export const userClickNextPageAction = () => ({
  type: ActionType.UserClickNextPage
});
export const userClickPrevPageAction = () => ({
  type: ActionType.UserClickPrevPage
});
export const userClickResendEmailAction = bankAccountToken => ({
  type: ActionType.UserClickResendEmail,
  bankAccountToken
});
export const userClickUseCheckingAction = () => ({
  type: ActionType.UserClickUseChecking
});

// Selectors
// =============================================================================
export const goNextPageDisabled = state => {
  return state.currentPage >= state.lastPage;
};
export const goPrevPageDisabled = state => {
  return state.currentPage <= 1;
};

// MISC
// =============================================================================

function getAllLocationDescription(countryCode, intl) {
  switch (countryCode) {
    case Country.JP:
      return intl.t('banking.unifiedBankAccounts.allLocations.description.JP', {
        htmlSafe: true
      });
    case Country.GB:
      return intl.t('banking.unifiedBankAccounts.allLocations.description.GB');
    default:
      return intl.t('banking.unifiedBankAccounts.allLocations.description.default');
  }
}
function getLinkAnAccountLabel(accountType, intl) {
  const noBankAccountTranslationKey = accountType === AccountType.CHECKING_ACCOUNT ? 'banking.unifiedBankAccounts.allLocations.locationRow.linkAnAccountOptional' : 'banking.unifiedBankAccounts.allLocations.locationRow.linkAnAccount';
  return intl.t(noBankAccountTranslationKey);
}
function getPendingBankAccountLabel(pendingBankAccount, intl) {
  const {
    bankName,
    accountNumberSuffix,
    type,
    state
  } = pendingBankAccount;
  const bankType = getBankAccountType(type, intl);
  const pendingBankAccountTranslationKey = state === BankAccountInfo.BankAccountState.AWAITING_2FA_CONFIRMATION ? 'banking.unifiedBankAccounts.allLocations.locationRow.awaitingTwoFactor' : 'banking.unifiedBankAccounts.allLocations.locationRow.pendingVerification';
  return intl.t(pendingBankAccountTranslationKey, {
    bankName,
    bankType,
    accountNumberSuffix
  });
}
function getFailedBankAccountLabel(linkedBankAccount, intl) {
  const {
    bankName,
    accountNumberSuffix,
    type
  } = linkedBankAccount;
  const bankType = getBankAccountType(type, intl);
  return intl.t('banking.unifiedBankAccounts.allLocations.locationRow.failedVerification', {
    bankName,
    bankType,
    accountNumberSuffix
  });
}
function markLinkedBankInMerchantBankAccounts(linkedBankAccount, merchantBankAccounts) {
  return merchantBankAccounts.map(bankAccountInfo => {
    const isLinkedBankAccount = Boolean(linkedBankAccount?.accountRoutingFideliusToken && bankAccountInfo.accountRoutingFideliusToken && linkedBankAccount?.accountRoutingFideliusToken === bankAccountInfo.accountRoutingFideliusToken);
    return {
      bankAccountInfo,
      isLinkedBankAccount
    };
  });
}

// Update
// =============================================================================

function handleAddExternalBankAccountReceivedAction(state) {
  return [{
    ...state,
    requiresRefresh: true
  }, null];
}
function handleInitAllLocationsAction(context, state, action) {
  const {
    unitBankAccountGroups,
    unitAccounts,
    merchantToken,
    isAccountOwner
  } = action;
  const {
    intl,
    countryCode
  } = context;
  const allLocationDescription = getAllLocationDescription(countryCode, intl);
  const allRows = unitAccounts.map(account => {
    assert(account.owner);
    const unitToken = account.owner.id;
    const {
      linkedBankAccount,
      pendingBankAccount,
      merchantBankAccounts
    } = unitBankAccountGroups[unitToken];
    const hasNoBankAccount = !linkedBankAccount && !pendingBankAccount;
    const isFailed = linkedBankAccount?.state === BankAccountInfo.BankAccountState.FAILED;
    return {
      accountType: getAccountType(account.type, countryCode, intl),
      failedBankAccountLabel: isFailed ? getFailedBankAccountLabel(linkedBankAccount, intl) : null,
      isAwaiting2FA: pendingBankAccount?.state === BankAccountInfo.BankAccountState.AWAITING_2FA_CONFIRMATION,
      isFailed,
      linkAnAccountLabel: hasNoBankAccount || isFailed ? getLinkAnAccountLabel(account.type, intl) : null,
      linkedBankAccount: linkedBankAccount,
      linkedBankAccountLabel: linkedBankAccount ? getLinkedBankAccountLabel(linkedBankAccount, intl) : null,
      locationName: account.name,
      merchantBankAccounts: markLinkedBankInMerchantBankAccounts(linkedBankAccount, merchantBankAccounts),
      pendingBankAccount: pendingBankAccount,
      pendingBankAccountLabel: pendingBankAccount ? getPendingBankAccountLabel(pendingBankAccount, intl) : null,
      unitToken
    };
  });
  return [{
    ...getInitialPaginatedState(state, allRows),
    merchantToken,
    allLocationDescription,
    isAccountOwner
  }, null];
}
function handleOpenLinkBankAccountFormAction(context, state, action) {
  const {
    unitToken,
    itFee,
    merchantToken
  } = action;
  // linkBankAccountService.linkBankAccount promise should always resolve.
  // When link bank account errors, we flash the error message and the user
  // will close the glass modal, which is a resolves the promise
  const work = () => {
    return from(context.linkBankAccountService.linkBankAccount({
      unitToken,
      itFee,
      merchantToken
    }).then(addExternalBankAccountReceivedAction, () => null));
  };
  return [state, work];
}
function handleResendEmailReceivedAction(context, state) {
  context.marketToasts.success(context.intl.t('banking.unifiedBankAccounts.toasts.emailResent'));
  return [state, null];
}
function handleResendEmailRejectedAction(context, state, action) {
  // TODO(jmah): fail toast
  context.sentry.captureException(action.error);
  return [state, null];
}
function handleUserClickAddExternalBankAccountAction(context, state, action) {
  const {
    unitToken
  } = action;
  const {
    merchantToken
  } = state;
  const work = () => {
    return from(context.depositsBatExperiment.isEligibleForCheckingUpsell(unitToken).then(isEligible => isEligible ? openCheckingUpsellModalAction(merchantToken, unitToken) : openLinkBankAccountFormAction(unitToken, '', merchantToken)));
  };
  return [state, work];
}
function handleUserClickCancelVerificationAction(state, action) {
  const {
    unitToken,
    pendingBankAccount
  } = action;
  return [state, () => initCancelVerificationModalAction(unitToken, pendingBankAccount)];
}
function handleUserClickChangeBankAccountAction(context, state, action) {
  const {
    unitToken,
    locationName,
    merchantBankAccount
  } = action;
  const {
    token
  } = merchantBankAccount;
  const selectedBankLabel = getLinkedBankAccountLabel(merchantBankAccount, context.intl);
  return [state, () => toggleChangeBankAccountModalAction(unitToken, selectedBankLabel, locationName, token)];
}
function handleUserClickResendEmailAction(context, state, action) {
  const {
    bankAccountToken
  } = action;
  const work = () => from(context.bankAccountApi.resendVerificationMessage(ResendVerificationMessageRequest.create({
    bankAccountToken
  })).then(resendEmailReceivedAction, resendEmailRejectedAction));
  return [state, work];
}
function handleUserClickUseChecking(context, state) {
  const work = () => {
    const cdpEvent = getEvent({
      action: EventAction.Click,
      feature: Feature.RecommendChecking
    });
    context.events.track({
      kind: 'CDP',
      event: {
        name: cdpEvent.name,
        properties: {
          ...cdpEvent.properties,
          applet: Applet.Settings,
          page: Page.Overview,
          target: Target.CheckingOnboarding
        }
      }
    });
    context.applicationRouter.transitionTo('checking');
    return EMPTY;
  };
  return [state, work];
}
export const update = context => (state, action) => {
  switch (action.type) {
    case ActionType.AddExternalBankAccountReceived:
      return handleAddExternalBankAccountReceivedAction(state);
    case ActionType.InitAllLocations:
      return handleInitAllLocationsAction(context, state, action);
    case ActionType.OpenLinkBankAccountForm:
      return handleOpenLinkBankAccountFormAction(context, state, action);
    case ActionType.ResendEmailReceived:
      return handleResendEmailReceivedAction(context, state);
    case ActionType.ResendEmailRejected:
      return handleResendEmailRejectedAction(context, state, action);
    case ActionType.UserClickAddExternalBankAccount:
      return handleUserClickAddExternalBankAccountAction(context, state, action);
    case ActionType.UserClickCancelVerification:
      return handleUserClickCancelVerificationAction(state, action);
    case ActionType.UserClickChangeBankAccount:
      return handleUserClickChangeBankAccountAction(context, state, action);
    case ActionType.UserClickNextPage:
      return handleUserClickNextPageAction(state);
    case ActionType.UserClickPrevPage:
      return handleUserClickPrevPageAction(state);
    case ActionType.UserClickResendEmail:
      return handleUserClickResendEmailAction(context, state, action);
    case ActionType.UserClickUseChecking:
      return handleUserClickUseChecking(context, state);
  }
};